import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import Cropper from "react-easy-crop";
import { apiGet, apiAuth, getToken, setToken, clearToken, PLATFORMS, API, uploadFile, mediaUrl } from "../lib/api";
import { readFile, getCroppedBlob } from "../lib/cropImage";
import { Loader2, LogOut, Save, ChevronDown, Music2, Disc3, ShoppingBag, Lock, Package, Upload, Crop, Download } from "lucide-react";

const LABEL = "font-tech text-[10px] tracking-[0.2em] text-[#ECE7DB]/60";
const INPUT = "bg-[#1c1512] border-2 border-[#ECE7DB]/20 px-3 py-2 font-body text-sm text-[#ECE7DB] focus:outline-none focus:border-[#CF9026] w-full";
const BTN = "font-tech tracking-[0.2em] text-xs bg-[#CF9026] text-[#15100E] px-5 py-3 hover:bg-[#ECE7DB] transition-colors flex items-center justify-center gap-2 disabled:opacity-50";

function UploadButton({ accept, label = "UPLOAD", onUploaded, testid, big = false, crop = false }) {
  const [loading, setLoading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropName, setCropName] = useState("");
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const inputRef = React.useRef(null);

  const doUpload = async (file) => {
    setLoading(true);
    try {
      const res = await uploadFile(file);
      onUploaded?.(res);
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (crop) {
      const src = await readFile(file);
      setCropName(file.name.replace(/\.[^.]+$/, "") + ".jpg");
      setCropPos({ x: 0, y: 0 });
      setZoom(1);
      setCropSrc(src);
    } else {
      await doUpload(file);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const confirmCrop = async () => {
    if (!pixels) return;
    setLoading(true);
    try {
      const blob = await getCroppedBlob(cropSrc, pixels, 1000);
      const file = new File([blob], cropName, { type: "image/jpeg" });
      const res = await uploadFile(file);
      onUploaded?.(res);
      toast.success("Image cropped & uploaded");
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
    setCropSrc(null);
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} onChange={handle} className="hidden" data-testid={testid} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={loading}
        className={`font-tech text-[10px] tracking-[0.2em] border-2 border-[#ECE7DB]/25 text-[#ECE7DB] px-3 py-2 hover:border-[#CF9026] hover:text-[#CF9026] transition-colors flex items-center gap-2 disabled:opacity-50 ${big ? "w-full justify-center py-3" : ""}`}
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {loading ? "UPLOADING…" : label}
      </button>

      {cropSrc && (
        <div className="fixed inset-0 z-[300] bg-[#15100E]/90 flex items-center justify-center p-4" data-testid="crop-modal">
          <div className="w-full max-w-md bg-[#1c1512] border-2 border-[#CF9026] p-5">
            <div className="flex items-center gap-2 text-[#CF9026] mb-3">
              <Crop size={16} />
              <p className="font-display font-extrabold text-lg text-[#ECE7DB]">CROP TO SQUARE</p>
            </div>
            <p className="font-tech text-[#ECE7DB]/45 text-[10px] tracking-widest mb-3">Drag to reposition · pinch/slider to zoom · keeps merch sharp on the wheel</p>
            <div className="relative w-full bg-black" style={{ height: 300 }}>
              <Cropper
                image={cropSrc}
                crop={cropPos}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid
                onCropChange={setCropPos}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => setPixels(areaPixels)}
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="font-tech text-[10px] text-[#ECE7DB]/50">ZOOM</span>
              <input
                data-testid="crop-zoom"
                type="range" min="1" max="3" step="0.01" value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-[#CF9026]"
              />
            </div>
            <div className="mt-4 flex gap-3">
              <button data-testid="crop-cancel" onClick={() => setCropSrc(null)} className="flex-1 font-tech text-[11px] tracking-[0.2em] border-2 border-[#ECE7DB]/25 text-[#ECE7DB] py-2.5 hover:border-[#ECE7DB] transition-colors">CANCEL</button>
              <button data-testid="crop-confirm" onClick={confirmCrop} disabled={loading} className={BTN + " flex-1"}>
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Crop size={14} />} CROP & UPLOAD
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Login({ onDone }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      setToken(data.token);
      onDone();
    } catch (e) {
      setErr(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#15100E] px-6 grain">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-[#1c1512] border-2 border-[#ECE7DB]/20 p-8">
        <div className="flex items-center gap-2 mb-6 text-[#CF9026]">
          <Lock size={18} />
          <h1 className="font-display font-extrabold stretch-text text-2xl text-[#ECE7DB]">ILLER ADMIN</h1>
        </div>
        <div className="space-y-4">
          <div>
            <label className={LABEL}>USERNAME</label>
            <input data-testid="admin-username" value={username} onChange={(e) => setUsername(e.target.value)} className={INPUT + " mt-1"} autoComplete="username" />
          </div>
          <div>
            <label className={LABEL}>PASSWORD</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT + " mt-1"} autoComplete="current-password" />
          </div>
          {err && <p data-testid="admin-login-error" className="font-tech text-[#e07a86] text-[11px]">{err}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className={BTN + " w-full"}>
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} LOG IN
          </button>
        </div>
      </form>
    </div>
  );
}

function ShipForm({ sessionId, onShipped }) {
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await apiAuth(`/admin/orders/${sessionId}/ship`, "POST", { carrier, tracking });
      toast.success("Marked as shipped — buyer emailed");
      onShipped?.();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  return (
    <div className="mt-3 pt-3 border-t border-[#ECE7DB]/10 flex items-end gap-2 flex-wrap">
      <div className="flex-1 min-w-[120px]">
        <label className={LABEL}>CARRIER</label>
        <input data-testid={`ship-carrier-${sessionId}`} value={carrier} onChange={(e) => setCarrier(e.target.value)} className={INPUT + " mt-1"} placeholder="USPS / UPS / FedEx" />
      </div>
      <div className="flex-1 min-w-[140px]">
        <label className={LABEL}>TRACKING #</label>
        <input data-testid={`ship-tracking-${sessionId}`} value={tracking} onChange={(e) => setTracking(e.target.value)} className={INPUT + " mt-1"} placeholder="Tracking number" />
      </div>
      <button data-testid={`ship-submit-${sessionId}`} onClick={submit} disabled={loading} className={BTN + " !py-2"}>
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />} MARK SHIPPED
      </button>
    </div>
  );
}

function MarkSentButton({ sessionId, onDone }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await apiAuth(`/admin/orders/${sessionId}/mark-sent`, "POST", {});
      toast.success("Marked as download sent");
      onDone?.();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  return (
    <button data-testid={`mark-sent-${sessionId}`} onClick={submit} disabled={loading} className={BTN + " !py-2"}>
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} MARK DOWNLOAD SENT
    </button>
  );
}


function TrackCard({ t, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-2 border-[#ECE7DB]/15 p-4">
      <div className="flex items-center gap-3">
        <span className="font-display font-extrabold text-[#CF9026] text-xl w-8 shrink-0">{String(t.n).padStart(2, "0")}</span>
        <input data-testid={`admin-track-title-${t.n}`} value={t.title} onChange={(e) => onChange({ ...t, title: e.target.value })} className={INPUT} placeholder="TITLE" />
        <input data-testid={`admin-track-feat-${t.n}`} value={t.feat || ""} onChange={(e) => onChange({ ...t, feat: e.target.value })} className={INPUT + " max-w-[180px]"} placeholder="FEAT (optional)" />
        <button type="button" onClick={() => setOpen((o) => !o)} className="font-tech text-[10px] tracking-[0.15em] text-[#ECE7DB]/60 hover:text-[#CF9026] flex items-center gap-1 shrink-0">
          LINKS <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {PLATFORMS.map((p) => (
            <div key={p.key}>
              <label className={LABEL}>{p.label.toUpperCase()}</label>
              <input
                data-testid={`admin-track-${t.n}-${p.key}`}
                value={(t.links && t.links[p.key]) || ""}
                onChange={(e) => onChange({ ...t, links: { ...(t.links || {}), [p.key]: e.target.value } })}
                className={INPUT + " mt-1"}
                placeholder={`${p.label} URL`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [merch, setMerch] = useState([]);
  const [albumLinks, setAlbumLinks] = useState({});
  const [bgVideo, setBgVideo] = useState("");
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("tracks");
  const [saving, setSaving] = useState("");

  const loadData = async () => {
    const [tk, mc, st, or] = await Promise.all([
      apiGet("/tracks"),
      apiAuth("/admin/merch", "GET"),
      apiGet("/settings"),
      apiAuth("/admin/orders", "GET"),
    ]);
    setTracks(tk);
    setMerch(mc);
    setAlbumLinks(st.album_links || {});
    setBgVideo(st.bg_video || "");
    setOrders(or);
  };

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    apiAuth("/auth/me", "GET")
      .then(() => { setAuthed(true); loadData(); })
      .catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const logout = () => { clearToken(); setAuthed(false); };

  const saveTracks = async () => {
    setSaving("tracks");
    try {
      await apiAuth("/admin/tracks", "PUT", { tracks });
      toast.success("Tracklist saved");
    } catch (e) { toast.error(e.message); if (e.message.includes("log in")) logout(); }
    setSaving("");
  };

  const saveAlbum = async () => {
    setSaving("album");
    try {
      await apiAuth("/admin/settings", "PUT", { album_links: albumLinks });
      toast.success("Album links saved");
    } catch (e) { toast.error(e.message); if (e.message.includes("log in")) logout(); }
    setSaving("");
  };

  const saveMerch = async () => {
    setSaving("merch");
    try {
      const payload = merch.map((m) => ({ ...m, price: parseFloat(m.price) || 0 }));
      await apiAuth("/admin/merch", "PUT", { merch: payload });
      toast.success("Merch saved");
    } catch (e) { toast.error(e.message); if (e.message.includes("log in")) logout(); }
    setSaving("");
  };

  const saveBgVideo = async (url) => {
    try {
      await apiAuth("/admin/settings", "PUT", { bg_video: url });
      setBgVideo(url);
      toast.success("Background video updated");
    } catch (e) { toast.error(e.message); if (e.message.includes("log in")) logout(); }
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#15100E]"><Loader2 className="animate-spin text-[#CF9026]" size={32} /></div>;
  }
  if (!authed) return <><Toaster theme="dark" position="top-center" /><Login onDone={() => { setAuthed(true); loadData(); }} /></>;

  const TABS = [
    { id: "tracks", label: "TRACKLIST", icon: Music2 },
    { id: "album", label: "ALBUM LINKS", icon: Disc3 },
    { id: "merch", label: "MERCH", icon: ShoppingBag },
    { id: "orders", label: "ORDERS", icon: Package },
  ];

  return (
    <div className="min-h-screen bg-[#15100E] text-[#ECE7DB] grain">
      <Toaster theme="dark" position="top-center" />
      <header className="flex items-center justify-between px-6 h-16 border-b-2 border-[#ECE7DB]/20 sticky top-0 bg-[#15100E] z-10">
        <h1 className="font-display font-extrabold stretch-text text-2xl">ILLER ADMIN</h1>
        <button data-testid="admin-logout" onClick={logout} className="font-tech text-[11px] tracking-[0.2em] flex items-center gap-2 hover:text-[#CF9026] transition-colors">
          <LogOut size={15} /> LOG OUT
        </button>
      </header>

      <div className="flex gap-2 px-6 py-4 border-b-2 border-[#ECE7DB]/10 sticky top-16 bg-[#15100E] z-10 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`font-tech text-[11px] tracking-[0.2em] px-4 py-2 border-2 flex items-center gap-2 transition-colors ${tab === t.id ? "bg-[#CF9026] text-[#15100E] border-[#CF9026]" : "border-[#ECE7DB]/20 hover:border-[#CF9026]"}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-24">
        {tab === "tracks" && (
          <div className="space-y-3">
            {tracks.map((t, i) => (
              <TrackCard key={t.n} t={t} onChange={(nt) => setTracks((arr) => arr.map((x, idx) => (idx === i ? nt : x)))} />
            ))}
            <div className="sticky bottom-4 pt-4">
              <button data-testid="admin-save-tracks" onClick={saveTracks} disabled={saving === "tracks"} className={BTN}>
                {saving === "tracks" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} SAVE TRACKLIST
              </button>
            </div>
          </div>
        )}

        {tab === "album" && (
          <div className="space-y-4 max-w-xl">
            <div className="border-2 border-[#ECE7DB]/15 p-4">
              <p className="font-tech text-[#CF9026] text-[11px] tracking-[0.25em] mb-2">BACKGROUND VIDEO</p>
              <p className="font-tech text-[#ECE7DB]/45 text-[11px] mb-3">Upload an MP4 to loop behind the site (muted). Replaces the current clip.</p>
              {bgVideo && (
                <video src={mediaUrl(bgVideo)} muted loop playsInline autoPlay className="w-full max-h-40 object-cover border border-[#ECE7DB]/20 mb-3" />
              )}
              <UploadButton
                accept="video/mp4,video/*"
                label={bgVideo ? "REPLACE VIDEO" : "UPLOAD VIDEO"}
                testid="admin-bgvideo-upload"
                big
                onUploaded={(res) => saveBgVideo(res.url)}
              />
            </div>
            <p className="font-tech text-[#ECE7DB]/50 text-[11px] tracking-widest">Album-wide streaming links shown on the home panel.</p>
            {PLATFORMS.map((p) => (
              <div key={p.key}>
                <label className={LABEL}>{p.label.toUpperCase()}</label>
                <input
                  data-testid={`admin-album-${p.key}`}
                  value={albumLinks[p.key] || ""}
                  onChange={(e) => setAlbumLinks((a) => ({ ...a, [p.key]: e.target.value }))}
                  className={INPUT + " mt-1"}
                  placeholder={`${p.label} album URL`}
                />
              </div>
            ))}
            <button data-testid="admin-save-album" onClick={saveAlbum} disabled={saving === "album"} className={BTN}>
              {saving === "album" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} SAVE ALBUM LINKS
            </button>
          </div>
        )}

        {tab === "merch" && (
          <div className="space-y-4">
            {merch.map((m, i) => (
              <div key={m.id} data-testid={`admin-merch-${m.id}`} className="border-2 border-[#ECE7DB]/15 p-4 grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 flex items-center gap-3">
                  <img src={mediaUrl(m.img)} alt="" className="h-12 w-12 object-cover border border-[#ECE7DB]/20" />
                  <input value={m.name} onChange={(e) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} className={INPUT} />
                  <UploadButton
                    accept="image/*"
                    label="IMAGE"
                    crop
                    testid={`admin-merch-upload-${m.id}`}
                    onUploaded={(res) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, img: res.url } : x))}
                  />
                </div>
                <div>
                  <label className={LABEL}>PRICE (USD)</label>
                  <input data-testid={`admin-merch-price-${m.id}`} value={m.price} onChange={(e) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x))} className={INPUT + " mt-1"} />
                </div>
                <div>
                  <label className={LABEL}>BADGE / NOTE</label>
                  <input value={m.note || ""} onChange={(e) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, note: e.target.value } : x))} className={INPUT + " mt-1"} />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>DESCRIPTION</label>
                  <input value={m.desc || ""} onChange={(e) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, desc: e.target.value } : x))} className={INPUT + " mt-1"} />
                </div>
                {m.digital && (
                  <div className="sm:col-span-2">
                    <label className={LABEL}>DIGITAL DOWNLOAD URL</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input data-testid={`admin-merch-download-${m.id}`} value={m.download_url || ""} onChange={(e) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, download_url: e.target.value } : x))} className={INPUT} />
                      <UploadButton
                        accept=".zip,application/zip"
                        label="ZIP"
                        testid={`admin-merch-zip-${m.id}`}
                        onUploaded={(res) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, download_url: res.url } : x))}
                      />
                    </div>
                    <p className="font-tech text-[#ECE7DB]/35 text-[10px] mt-1">Paste a link (Dropbox etc.) or upload the album ZIP directly.</p>
                  </div>
                )}
                <label className="flex items-center gap-2 font-tech text-[11px] tracking-[0.15em] cursor-pointer sm:col-span-2">
                  <input data-testid={`admin-merch-available-${m.id}`} type="checkbox" checked={m.available !== false} onChange={(e) => setMerch((arr) => arr.map((x, idx) => idx === i ? { ...x, available: e.target.checked } : x))} className="h-4 w-4 accent-[#CF9026]" />
                  IN STOCK / AVAILABLE {m.available === false && <span className="text-[#e07a86]">(SOLD OUT)</span>}
                </label>
              </div>
            ))}
            <button data-testid="admin-save-merch" onClick={saveMerch} disabled={saving === "merch"} className={BTN}>
              {saving === "merch" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} SAVE MERCH
            </button>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4">
            {(() => {
              const visible = orders.filter((o) => o.payment_status === "paid" || o.kind === "album_manual");
              return (
            <>
            <div className="flex items-center justify-between">
              <p className="font-tech text-[#ECE7DB]/50 text-[11px] tracking-widest">{visible.length} ORDER{visible.length === 1 ? "" : "S"} · NEWEST FIRST</p>
              <button data-testid="admin-orders-refresh" onClick={loadData} className="font-tech text-[10px] tracking-[0.2em] border-2 border-[#ECE7DB]/20 px-3 py-1.5 hover:border-[#CF9026] transition-colors">REFRESH</button>
            </div>
            {visible.length === 0 && <p className="font-body text-[#ECE7DB]/40 text-sm py-8 text-center">No orders yet.</p>}
            {visible.map((o) => {
              const paid = o.payment_status === "paid";
              const dt = o.created_at ? new Date(o.created_at) : null;
              const s = o.shipping || {};
              const isPwyw = o.kind === "album_pwyw";
              const isManual = o.kind === "album_manual";
              const typeLabel = isManual ? "MANUAL" : isPwyw ? "NAME YOUR PRICE" : "MERCH";
              const typeColor = isManual ? "bg-[#7A1623] text-[#ECE7DB]" : isPwyw ? "bg-[#CF9026] text-[#15100E]" : "bg-[#243B52] text-[#ECE7DB]";
              const isDigital = isManual || isPwyw || (o.has_digital && !s.address);
              const sent = o.fulfillment_status === "download_sent";
              return (
                <div key={o.session_id} data-testid={`admin-order-${o.session_id}`} className="border-2 border-[#ECE7DB]/15 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-tech text-[9px] tracking-[0.2em] px-2 py-1 ${typeColor}`}>{typeLabel}</span>
                      <span className={`font-tech text-[10px] tracking-[0.2em] px-2 py-1 ${paid ? "bg-[#2e7d32] text-[#ECE7DB]" : isManual ? "bg-[#ECE7DB]/15 text-[#ECE7DB]/80" : o.status === "expired" ? "bg-[#7A1623] text-[#ECE7DB]" : "bg-[#ECE7DB]/15 text-[#ECE7DB]/70"}`}>
                        {paid ? "PAID" : isManual ? "AWAITING PAYMENT" : (o.payment_status || "PENDING").toUpperCase()}
                      </span>
                      {o.method && <span className="font-tech text-[9px] tracking-[0.2em] px-2 py-1 border-2 border-[#ECE7DB]/25 text-[#ECE7DB]/70">{o.method.toUpperCase()}</span>}
                      <span className="font-display font-extrabold text-lg">${Number(o.amount || 0).toFixed(2)}</span>
                    </div>
                    <span className="font-tech text-[#ECE7DB]/45 text-[11px]">{dt ? dt.toLocaleString() : ""}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="font-tech text-[9px] tracking-[0.25em] text-[#CF9026] mb-1">ITEMS</p>
                      <ul className="space-y-1">
                        {(o.items || []).map((it, idx) => (
                          <li key={idx} className="font-body text-sm text-[#ECE7DB]/85">
                            {it.qty} × {it.name}{it.size ? ` — SIZE ${it.size}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="font-tech text-[9px] tracking-[0.25em] text-[#CF9026] mb-1">{isDigital ? "SEND DOWNLOAD TO" : "SHIPPING"}</p>
                      {s.name || s.address || o.email ? (
                        <div className="font-body text-sm text-[#ECE7DB]/85 leading-snug">
                          {s.name && <p>{s.name}</p>}
                          {(o.email || s.email) && <p className="text-[#ECE7DB] font-semibold select-all" data-testid={`order-email-${o.session_id}`}>{o.email || s.email}</p>}
                          {s.address && <p>{s.address}</p>}
                          {(s.city || s.state || s.zip) && <p>{[s.city, s.state, s.zip].filter(Boolean).join(", ")}</p>}
                          {s.country && <p>{s.country}</p>}
                        </div>
                      ) : (
                        <p className="font-body text-sm text-[#ECE7DB]/40">Digital only — no shipping</p>
                      )}
                    </div>
                  </div>
                  {s.address ? (
                    o.fulfillment_status === "shipped" ? (
                      <div className="mt-3 pt-3 border-t border-[#ECE7DB]/10 flex items-center gap-2 flex-wrap">
                        <span className="font-tech text-[10px] tracking-[0.2em] bg-[#243B52] text-[#ECE7DB] px-2 py-1">SHIPPED</span>
                        <span className="font-body text-sm text-[#ECE7DB]/70">{o.carrier} {o.tracking && `· ${o.tracking}`}</span>
                      </div>
                    ) : (
                      <ShipForm sessionId={o.session_id} onShipped={loadData} />
                    )
                  ) : isDigital ? (
                    <div className="mt-3 pt-3 border-t border-[#ECE7DB]/10 flex items-center gap-3 flex-wrap">
                      {sent ? (
                        <span className="font-tech text-[10px] tracking-[0.2em] bg-[#2e7d32] text-[#ECE7DB] px-2 py-1">DOWNLOAD SENT</span>
                      ) : (
                        <MarkSentButton sessionId={o.session_id} onDone={loadData} />
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
            </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
}
